import type { AdminClientFull, AdminClientRow, ClientStatus } from './types';

export interface ListQuery {
  status: ClientStatus[];
  search: string;
  page: number;
  pageSize: number;
}

export interface ListResult {
  rows: AdminClientRow[];
  total: number;
  page: number;
  pageSize: number;
}

function q(query: ListQuery): string {
  const p = new URLSearchParams();
  for (const s of query.status) p.append('status', s);
  if (query.search.trim() !== '') p.set('search', query.search.trim());
  p.set('page', String(query.page));
  p.set('page_size', String(query.pageSize));
  return p.toString();
}

export async function fetchClients(query: ListQuery): Promise<ListResult> {
  const res = await fetch(`/api/admin/clients?${q(query)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ListResult;
}

interface ClientRawResponse {
  client: {
    id: string;
    name: string;
    vatNumber: string | null;
    invoiceEmail: string | null;
    address: string | null;
    paymentTermsDays: number;
    hourlyRate: string | null;
    defaultSalesRepId: string | null;
    status: ClientStatus;
    trustpilotReviewReceived: boolean;
    cancellationNoticeMonths: number;
    notes: string | null;
    acquisitionSource: string | null;
    customerSince: string | null;
    clickupCrmTaskId: string | null;
    updatedAt: string;
  };
}

export async function fetchClient(id: string): Promise<AdminClientFull> {
  const res = await fetch(`/api/admin/clients/${id}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as ClientRawResponse;
  return json.client;
}

export interface PatchBody {
  name?: string;
  vat_number?: string | null;
  invoice_email?: string | null;
  address?: string | null;
  payment_terms_days?: number;
  hourly_rate?: string | null;
  default_sales_rep_id?: string | null;
  status?: ClientStatus;
  trustpilot_review_received?: boolean;
  cancellation_notice_months?: number;
  notes?: string | null;
}

export async function patchClient(id: string, patch: PatchBody): Promise<void> {
  const res = await fetch(`/api/admin/clients/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
}
